import { useCallback, useContext, useEffect, useRef, useState } from 'react'
import { Controller, SubmitHandler, useForm, useWatch } from 'react-hook-form'

import { zodResolver } from '@hookform/resolvers/zod'

import { useCallMutation } from '@/common/api/client'
import { apiPutFetcher } from '@/common/api/fetcher'
import { useRetrainHistoryGetApi } from '@/common/api/modules/retrain'
import {
  CleanupStuckRetrainResponse,
  CleanupStuckRetrainVars,
  GetRetrainHistoryResponse,
  MarkRetrainTimeoutResponse,
  MarkRetrainTimeoutVars,
  RetrainApiPath,
  RetrainModelsResponse,
  RetrainModelsVars
} from '@/common/api/params/retrain'
import {
  GetModelTuningResponse,
  SiteApiPath,
  UpdateModelTuningVars
} from '@/common/api/params/site'
import { useFormErrorMessages, useMessageStates } from '@/common/app/message'
import { useShowSavedToast } from '@/common/app/toast'
import { dateTimeStr } from '@/common/form/date'
import { useTimer } from '@/common/util/timer'

import Badge, { BadgeType } from '@/components/view/atoms/badge/Badge'
import Button, { ButtonColor } from '@/components/view/atoms/button/Button'
import IconButton from '@/components/view/atoms/button/IconButton'
import FormLabel from '@/components/view/atoms/form/FormLabel'
import Heading from '@/components/view/atoms/heading/Heading'
import Icon, { IconType } from '@/components/view/atoms/icon/Icon'
import Slider from '@/components/view/atoms/slider/Slider'
import SimpleTable from '@/components/view/atoms/table/SimpleTable'
import TableHeadItem from '@/components/view/atoms/table/TableHeadItem'
import TableRow from '@/components/view/atoms/table/TableRow'
import TableRowItem from '@/components/view/atoms/table/TableRowItem'
import Text, { TextColor } from '@/components/view/atoms/text/Text'
import TextField, { TextBoxSize } from '@/components/view/atoms/textfield/TextField'
import Area from '@/components/view/layouts/area/Area'
import AreaContent from '@/components/view/layouts/area/AreaContent'
import InsideArea, { BgColor } from '@/components/view/layouts/area/InsideArea'
import Line from '@/components/view/layouts/area/Line'
import {
  DisclosurePositionHorizon,
  DisclosurePositionVertical,
  DisclosureSize
} from '@/components/view/layouts/disclosure/Disclosure'
import DisclosureWrapper from '@/components/view/layouts/disclosure/DisclosureWrapper'
import FormRow from '@/components/view/layouts/form/FormRow'
import ItemAlign from '@/components/view/layouts/item/ItemAlign'
import Content, { ContentType } from '@/components/view/layouts/page/Content'
import ContentSideFrame from '@/components/view/layouts/page/ContentSideFrame'
import Side from '@/components/view/layouts/page/Side'
import MessageBar, { MessageType } from '@/components/view/molecules/bar/MessageBar'
import TopBar, { TopBarStatus } from '@/components/view/molecules/bar/TopBar'
import DescriptionDisclosure from '@/components/view/molecules/disclosure/DescriptionDisclosure'
import MetricItem from '@/components/view/molecules/metric/MetricItem'
import PageTitle from '@/components/view/molecules/title/PageTitle'
import TooltipWrapper from '@/components/view/utils/tooltip/TooltipWrapper'

import FormError from '@/components/container/form/widgets/FormError'
import FormMessageList from '@/components/container/form/widgets/FormMessageList'
import FormTextField, { FormTextFieldType } from '@/components/container/form/widgets/FormTextField'
import { useAppHeaderHiding } from '@/components/container/layout/AppHeaderProvider'
import AppTitleBadge from '@/components/container/layout/AppTitleBadge'
import { TrainingHistoryModalContext } from '@/components/container/modal/trainingHistory/TrainingHistoryModalProvider'
import W2ModeMessage from '@/components/container/modules/master/master/W2ModeMessage'

import {
  DEFAULT_TUNING_SCHEMA,
  ModelTuningFormSchemaType,
  convertTuningResponseToSchema,
  convertTuningSchemaToVars,
  modelTuningSchema,
  normalizeHybridWeights
} from './modelTuningSchema'

// ---- Types ----

interface RetrainMsg {
  type: MessageType
  text: string
}

// ---- Helpers ----

const parseJsonField = <T,>(field: unknown, fallback: Record<string, unknown> = {}): T => {
  if (field === null || field === undefined) return fallback as T
  if (typeof field === 'object') return field as T
  if (typeof field === 'string') {
    try {
      return JSON.parse(field) as T
    } catch {
      return fallback as T
    }
  }
  return fallback as T
}

// ---- Sub-components ----

const HelpBox = ({ text }: { text: string }) => (
  <div className='rounded-lg border border-indigo-200 bg-indigo-50 p-4 text-[12px] leading-relaxed whitespace-pre-line text-slate-600'>
    {text}
  </div>
)

interface DataStructurePanelProps {
  isW2Full: boolean
}

const DataStructurePanel = ({ isW2Full }: DataStructurePanelProps) => (
  <Area>
    <Heading
      rightNode={
        <Badge badgeType={isW2Full ? BadgeType.SQUARE_BRAND : BadgeType.SQUARE_SECONDARY} size={14}>
          {isW2Full ? 'W2完全連動' : 'Basicモード'}
        </Badge>
      }
      subText='モデル再学習時に各エンジンが参照するデータソースです。'
    >
      学習活用データ構造
    </Heading>
    <AreaContent>
      {isW2Full && (
        <W2ModeMessage message='W2Commerceの14マスタデータと連動し、より精度の高いレコメンドを実現します。 配信タグには item_variation_id と brand_id が追加され、バリエーション・ブランド別のトラッキングが可能になります。' />
      )}
      <div>
        <Text size={14} weight='bold'>
          共通データソース（全モード）
        </Text>
        <SimpleTable>
          <thead>
            <TableRow head noBorder>
              <TableHeadItem type='thin' width={144}>
                <Text size={12} weight='bold' color={TextColor.SECONDARY}>
                  テーブル
                </Text>
              </TableHeadItem>
              <TableHeadItem type='thin'>
                <Text size={12} weight='bold' color={TextColor.SECONDARY}>
                  活用項目
                </Text>
              </TableHeadItem>
              <TableHeadItem type='thin' width={166}>
                <Text size={12} weight='bold' color={TextColor.SECONDARY}>
                  対象エンジン
                </Text>
              </TableHeadItem>
            </TableRow>
          </thead>
          <tbody>
            {[
              ['行動ログ (Event)', 'view, cart, purchase のスコアリング', 'Collaborative, Neural'],
              [
                '受注履歴 (OrderHistory)',
                'item_id, quantity, customer_id',
                'Collaborative, Neural'
              ],
              ['商品マスタ (Product)', 'name, category, description, tags', 'Content-Based'],
              [
                'ユーザー統合 (UserIdentity)',
                'visitor_id ↔ customer_id マッピング',
                'Collaborative, Neural'
              ]
            ].map(([table, items, engines]) => (
              <TableRow key={table}>
                <TableRowItem>
                  <Text size={12}>{table}</Text>
                </TableRowItem>
                <TableRowItem>
                  <Text size={12}>{items}</Text>
                </TableRowItem>
                <TableRowItem>
                  <Text size={12}>{engines}</Text>
                </TableRowItem>
              </TableRow>
            ))}
          </tbody>
        </SimpleTable>
      </div>
      {isW2Full && (
        <div>
          <ItemAlign align='center' gapX={4}>
            <Text size={14} weight='bold'>
              追加データ
            </Text>
            <Badge badgeType={BadgeType.SQUARE_BRAND_SECONDARY} size={14}>
              W2Commerce完全連動モード
            </Badge>
          </ItemAlign>
          <SimpleTable>
            <thead>
              <TableRow head noBorder>
                <TableHeadItem type='thin' width={144}>
                  <Text size={12} weight='bold' color={TextColor.SECONDARY}>
                    W2マスタ
                  </Text>
                </TableHeadItem>
                <TableHeadItem type='thin'>
                  <Text size={12} weight='bold' color={TextColor.SECONDARY}>
                    活用項目
                  </Text>
                </TableHeadItem>
                <TableHeadItem type='thin' width={166}>
                  <Text size={12} weight='bold' color={TextColor.SECONDARY}>
                    対象エンジン
                  </Text>
                </TableHeadItem>
              </TableRow>
            </thead>
            <tbody>
              {[
                [
                  'W2商品マスタ',
                  'outline, desc_detail1-4, maker_name, catchcopy, search_keyword',
                  'Content-Based (TF-IDF)'
                ],
                ['W2商品マスタ', 'brand_id1-5, category_id1-5', 'Content-Based (TF-IDF)'],
                [
                  'W2商品マスタ',
                  'related_product_id_cs1-5 (クロスセル), _us1-5 (アップセル)',
                  'Collaborative, Neural'
                ],
                ['W2商品ブランド', 'brand_name', 'Content-Based (TF-IDF)'],
                ['W2商品カテゴリ', 'category_name', 'Content-Based (TF-IDF)'],
                ['W2商品タグ', 'product_id (タグ付けフラグ)', 'Content-Based (TF-IDF)'],
                [
                  'W2受注マスタ',
                  'user_id, advcode_first, member_rank_id (ユーザー統合に使用)',
                  'Collaborative, Neural'
                ],
                [
                  'W2受注明細',
                  'product_id, item_quantity, product_price (価格ブースト)',
                  'Collaborative, Neural'
                ],
                ['W2商品在庫', 'stock (バリエーション別max在庫で除外判定)', '全エンジン (フィルタ)']
              ].map(([table, items, engines]) => (
                <TableRow key={`${table}-${items}`}>
                  <TableRowItem>
                    <Text size={12}>{table}</Text>
                  </TableRowItem>
                  <TableRowItem>
                    <Text size={12}>{items}</Text>
                  </TableRowItem>
                  <TableRowItem>
                    <Text size={12}>{engines}</Text>
                  </TableRowItem>
                </TableRow>
              ))}
            </tbody>
          </SimpleTable>
        </div>
      )}
      <FormRow direction='vertical' gap={4}>
        <Text size={14} weight='bold' lineHeight={150}>
          スコアリング体系
        </Text>
        <ItemAlign gapX={16}>
          <MetricItem label='閲覧' value='1.0' />
          <MetricItem label='カート' value='3.0' />
          <MetricItem label='購入' value='5.0' />
          <MetricItem label='クロスセル' value='4.0' />
          <MetricItem label='アップセル' value='3.5' />
          <MetricItem label='高額商品(1万円+)' value='+1.0' />
        </ItemAlign>
      </FormRow>
    </AreaContent>
  </Area>
)

const ModelTrainingHelp = () => {
  return (
    <DescriptionDisclosure
      horizon={DisclosurePositionHorizon.LEFT}
      vertical={DisclosurePositionVertical.BOTTOM}
    >
      ■再学習とは
      <br />
      上記のパラメータ設定と、蓄積されたユーザー行動データ（閲覧・カート・購入）を元に、レコメンドモデルを一から作り直します。
      <br />
      <br />■ いつ再学習すべきか <br />
      ・パラメータを変更した後（保存だけでは反映されません）
      <br />
      ・新商品を大量に追加した後（再学習しないと新商品がレコメンドに出ません）
      <br />
      ・新規ユーザーが増えた後（新規ユーザーの行動が反映されません）
      <br />
      <br /> ■ 自動再学習
      <br />
      前回の再学習から1週間が経過すると、毎日04:00（JST）に自動で再学習が実行されます。
      <br />
      <br /> ■ 再学習の所要時間
      <br />
      データ量により異なります。商品数1,000件・行動ログ10万件程度で数十秒〜数分です。
      <br />
      <br /> ■ 注意事項 ・再学習中もレコメンド配信は継続します（前回のモデルが使われます）`
    </DescriptionDisclosure>
  )
}

interface RetrainProgressBarProps {
  historyData: GetRetrainHistoryResponse
  siteId: number
  targetHistoryId: number
  onComplete(): void
}

const RetrainProgressBar = ({
  historyData,
  siteId,
  targetHistoryId,
  onComplete
}: RetrainProgressBarProps) => {
  const [msg, setMsg] = useState<RetrainMsg | null>(null)

  // Retrain timeout (10 minutes)
  useEffect(() => {
    confirmMessage()
    const timer = setTimeout(
      async () => {
        try {
          if (targetHistoryId) {
            await triggerMarkTimeout({ site_id: siteId, history_id: targetHistoryId })
          }
          await triggerCleanupStuck({ site_id: siteId })
        } catch {}
        setMsg({
          type: MessageType.CRITICAL,
          text: '再学習のタイムアウト（10分以上）。履歴にエラーとして記録しました。'
        })
        onComplete()
      },
      10 * 60 * 1000
    )
    return () => clearTimeout(timer)
  }, [siteId, targetHistoryId, onComplete])

  const { trigger: triggerMarkTimeout } = useCallMutation<
    MarkRetrainTimeoutResponse,
    MarkRetrainTimeoutVars
  >(RetrainApiPath.MARK_RETRAIN_TIMEOUT, { errorNotShown: true })

  const { trigger: triggerCleanupStuck } = useCallMutation<
    CleanupStuckRetrainResponse,
    CleanupStuckRetrainVars
  >(RetrainApiPath.CLEANUP_STUCK_RETRAIN, { errorNotShown: true })

  const confirmMessage = () => {
    const target = targetHistoryId
      ? historyData.find((h) => h.id === targetHistoryId)
      : historyData[0]

    if (!target || target.status === 'processing') return

    const detail = parseJsonField<Record<string, unknown>>(target.result_detail)
    if (target.status === 'failed') {
      const errorMsg = typeof detail.error === 'string' ? detail.error : '不明なエラー'
      setMsg({ type: MessageType.CRITICAL, text: `再学習が失敗しました: ${errorMsg}` })
    } else {
      const durationSec = Math.round((target.duration_ms ?? 0) / 1000)
      const memoryMb =
        detail.memory_mb && typeof detail.memory_mb === 'object'
          ? ` / メモリ: ${(detail.memory_mb as { peak?: number }).peak ?? 0}MB`
          : ''
      const engines = Object.entries(detail)
        .filter(([k]) => k !== 'memory_mb')
        .map(([k, v]) => {
          const status =
            typeof v === 'object' && v !== null && 'status' in v
              ? (v as { status: string }).status
              : String(v)
          return `${k}: ${status}`
        })
        .join(', ')
      setMsg({
        type: MessageType.SUCCESS,
        text: `再学習完了（${durationSec}秒${memoryMb}）${engines}`
      })
    }
  }

  useTimer(() => {
    confirmMessage()
  }, 3000)

  return (
    msg && (
      <>
        <MessageBar type={msg.type}>
          <div className='m-1 flex flex-col gap-2'>
            <Text size={14} weight='bold' color={TextColor.INFO}>
              {msg.text}
            </Text>
          </div>
        </MessageBar>
      </>
    )
  )
}

// ---- Props ----

interface Props {
  tuningData: GetModelTuningResponse
  siteId: number
  isW2Full: boolean
  mutateTuning: (data: GetModelTuningResponse, revalidate?: boolean) => void
}

// ---- Main Form ----

const ModelTuningForm = ({ tuningData, siteId, isW2Full, mutateTuning }: Props) => {
  const {
    register,
    handleSubmit,
    control,
    reset,
    setValue,
    getValues,
    formState: { isSubmitting, isDirty, errors }
  } = useForm<ModelTuningFormSchemaType>({
    resolver: zodResolver(modelTuningSchema),
    values: convertTuningResponseToSchema(tuningData)
  })

  useAppHeaderHiding(isDirty)
  const [messages, setMessages] = useMessageStates()
  useFormErrorMessages(errors, setMessages, {})

  const [retraining, setRetraining] = useState(false)

  const targetHistoryId = useRef<number | null>(null)
  const retrainStartTime = useRef<number | null>(null)

  const { data: historyData, mutate: mutateHistory } = useRetrainHistoryGetApi(
    { site_id: siteId },
    { refreshInterval: retraining ? 3000 : 0 }
  )

  const { trigger: triggerRetrain, isMutating: isRetrainMutating } = useCallMutation<
    RetrainModelsResponse,
    RetrainModelsVars
  >(RetrainApiPath.RETRAIN_MODELS, {
    onSuccess: (data) => {
      targetHistoryId.current = data.history_id ?? null
      retrainStartTime.current = Date.now()
      setRetraining(true)
    },
    onError: () => {
      setRetraining(false)
    },
    errorNotShown: true
  })

  const showSaved = useShowSavedToast({ editing: true })
  const { trigger: triggerSave, isMutating: saving } = useCallMutation<
    GetModelTuningResponse,
    UpdateModelTuningVars
  >(
    SiteApiPath.UPDATE_MODEL_TUNING,
    {
      onSuccess: (data) => {
        mutateTuning(data, false)
        showSaved('モデルチューニングの設定')
      }
    },
    apiPutFetcher
  )

  const onSubmit: SubmitHandler<ModelTuningFormSchemaType> = (data) => {
    triggerSave(convertTuningSchemaToVars(siteId, data))
  }

  const handleRetrain = useCallback(async () => {
    setRetraining(true)
    await triggerRetrain({ site_id: siteId })
  }, [siteId, triggerRetrain])

  const handleReset = useCallback(() => {
    Object.keys(DEFAULT_TUNING_SCHEMA).forEach((key) => {
      // @ts-ignore
      setValue(key, DEFAULT_TUNING_SCHEMA[key], { shouldDirty: true })
    })
  }, [setValue])

  const { showModal: showTrainingHistoryModal } = useContext(TrainingHistoryModalContext)
  const svdKnnRatio = useWatch({ control, name: 'collaborative.svd_knn_ratio' })
  // svd_knn_ratio は 0〜1 の値で保存する。KNN 側も同じ 0〜1 の値で出す（百分率にしない）
  const knnRatio = Math.round((1 - (Number.isFinite(svdKnnRatio) ? svdKnnRatio : 0)) * 100) / 100

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <PageTitle
        title='モデルチューニング'
        badgeNode={<AppTitleBadge type='recommend' />}
        description='レコメンドエンジンのパラメータをサイト単位で調整します。'
        rightNode={
          <>
            {historyData && historyData.length > 0 && (
              <Text size={12} color={TextColor.SECONDARY}>
                最終モデル学習: {dateTimeStr(historyData[0].created_at)}
              </Text>
            )}
            <Button
              square
              size='large'
              color={ButtonColor.SECONDARY}
              onClick={() => showTrainingHistoryModal({ siteId })}
            >
              <Icon type={IconType.HISTORY} />
            </Button>
            <Button
              type='button'
              size='large'
              color={ButtonColor.SECONDARY}
              onClick={handleReset}
              disabled={isRetrainMutating}
            >
              デフォルトに戻す
            </Button>
            <Button
              type='button'
              size='large'
              color={ButtonColor.PRIMARY}
              onClick={handleRetrain}
              loading={isRetrainMutating}
            >
              モデル再学習
            </Button>
            <ModelTrainingHelp />
          </>
        }
      />
      {isDirty && (
        <TopBar
          status={TopBarStatus.CHANGE}
          captionText={'未反映の変更があります'}
          onCancel={() => reset(convertTuningResponseToSchema(tuningData))}
          loading={isSubmitting}
          withSide
        />
      )}
      <div className='mb-4 flex flex-col gap-4'>
        <MessageBar type={MessageType.WARNING}>
          <Text size={14} weight='bold' color={TextColor.CAUTION}>
            パラメータ変更後はモデルの再学習が必要です
          </Text>
          <br />
          <Text size={12}>
            保存のみでは新しい設定はレコメンドに反映されません。保存後、右上の「モデル再学習」ボタンを手動実行してください。
          </Text>
        </MessageBar>
        <FormMessageList messageStates={messages} setMessageStates={setMessages} />
        {retraining && (
          <RetrainProgressBar
            historyData={historyData ?? []}
            siteId={siteId}
            targetHistoryId={targetHistoryId.current ?? 0}
            onComplete={() => {
              setRetraining(false)
              mutateHistory()
            }}
          />
        )}
      </div>
      <ContentSideFrame>
        <Content type={ContentType.TWO_COLUMNS_LEFT}>
          <Area>
            <Heading>エンジンパラメータ調整</Heading>
            <AreaContent>
              <FormRow size={TextBoxSize.FULL} direction='vertical' gap={2}>
                <Text tag='h3' weight='bold' lineHeight={150}>
                  協調フィルタリング (SVD + KNN)
                </Text>
                <Text size={12}>
                  「この商品を見た人はこれも見ています」のような、ユーザー行動パターンの類似性から推薦を行うエンジンです。
                </Text>
              </FormRow>
              <FormRow>
                <FormRow direction='vertical' gap={0} size={TextBoxSize.LARGE}>
                  <FormRow align='center' gap={4}>
                    <FormLabel
                      help={
                        'SVDの因子数（次元数）。大きいほど複雑な嗜好パターンを捉えられます。商品数1,000以下なら30〜50、それ以上なら50〜100が目安です。'
                      }
                      fixWidth={null}
                    >
                      n_factors
                    </FormLabel>
                    <Text size={10} color={TextColor.SORA}>
                      潜在因子数
                    </Text>
                  </FormRow>
                  <FormTextField
                    type={FormTextFieldType.NUMBER}
                    size={TextBoxSize.SMALL}
                    register={register('collaborative.n_factors', { valueAsNumber: true })}
                    error={!!errors.collaborative?.n_factors}
                  />
                  <FormError message={errors.collaborative?.n_factors?.message} />
                </FormRow>
                <FormRow direction='vertical' gap={0} size={TextBoxSize.LARGE}>
                  <FormRow align='center' gap={4}>
                    <FormLabel
                      help={
                        'SVDとKNNの混合比率（SVD側の割合）を0〜1で指定します。0.6ならSVD 0.6・KNN 0.4。迷ったら0.6のままで問題ありません。'
                      }
                      fixWidth={null}
                    >
                      svd_knn_ratio
                    </FormLabel>
                    <Text size={10} color={TextColor.SORA}>
                      SVDの比率
                    </Text>
                  </FormRow>
                  <FormRow align='center' gap={2}>
                    <Text>SVD</Text>
                    <FormTextField
                      type={FormTextFieldType.NUMBER}
                      size={TextBoxSize.X_SMALL}
                      register={register('collaborative.svd_knn_ratio', { valueAsNumber: true })}
                      error={!!errors.collaborative?.svd_knn_ratio}
                    />
                    <Text>：KNN {knnRatio}</Text>
                  </FormRow>
                  <FormError message={errors.collaborative?.svd_knn_ratio?.message} />
                </FormRow>
                <FormRow direction='vertical' gap={0} size={TextBoxSize.LARGE}>
                  <FormRow align='center' gap={4}>
                    <FormLabel
                      help={
                        'KNNで参照する、似ている商品の数。商品ごとに類似度の高い商品をこの数だけ使い、訪問者が行動した商品の評価を類似度で重み付けして平均します。多いほど幅広い商品が手がかりになりますが、似ていない商品も混ざりやすくなります。20〜60が一般的です。'
                      }
                      fixWidth={null}
                    >
                      knn_neighbors
                    </FormLabel>
                    <Text size={10} color={TextColor.SORA}>
                      参考にする商品の数
                    </Text>
                  </FormRow>
                  <FormTextField
                    type={FormTextFieldType.NUMBER}
                    size={TextBoxSize.SMALL}
                    register={register('collaborative.knn_neighbors', { valueAsNumber: true })}
                    error={!!errors.collaborative?.knn_neighbors}
                  />
                  <FormError message={errors.collaborative?.knn_neighbors?.message} />
                </FormRow>
              </FormRow>
              <FormRow size={TextBoxSize.FULL} direction='vertical' gap={2}>
                <Text weight='bold' lineHeight={150}>
                  アクションスコア
                </Text>
                <Text size={12}>ユーザーの各行動にどれだけの重みを付けるかを設定します。</Text>
              </FormRow>
              <InsideArea color={BgColor.SECONDARY} fit>
                <FormRow size={TextBoxSize.FIT}>
                  <FormRow gap={4}>
                    <FormRow direction='vertical' gap={4} size={TextBoxSize.FIT}>
                      <FormRow size={TextBoxSize.SMALL}>
                        <FormLabel
                          fixWidth={null}
                          help={'商品ページを閲覧した時のスコア。基準値として1.0が推奨です。'}
                        >
                          view
                        </FormLabel>
                      </FormRow>
                      <Text color={TextColor.SORA} size={10}>
                        ページ閲覧スコア
                      </Text>
                    </FormRow>
                    <FormTextField
                      type={FormTextFieldType.NUMBER}
                      size={TextBoxSize.SMALL}
                      register={register('collaborative.action_scores.view', {
                        valueAsNumber: true
                      })}
                      error={!!errors.collaborative?.action_scores?.view}
                    />
                  </FormRow>
                  <FormRow gap={4}>
                    <FormRow direction='vertical' gap={4} size={TextBoxSize.FIT}>
                      <FormRow size={TextBoxSize.SMALL}>
                        <FormLabel
                          fixWidth={null}
                          help={
                            'カートに追加した時のスコア。閲覧より購買意欲が高い行動なので、2〜4倍程度が推奨です。'
                          }
                        >
                          cart
                        </FormLabel>
                      </FormRow>
                      <Text color={TextColor.SORA} size={10}>
                        カート追加スコア
                      </Text>
                    </FormRow>
                    <FormTextField
                      type={FormTextFieldType.NUMBER}
                      size={TextBoxSize.SMALL}
                      register={register('collaborative.action_scores.cart', {
                        valueAsNumber: true
                      })}
                      error={!!errors.collaborative?.action_scores?.cart}
                    />
                  </FormRow>
                  <FormRow gap={4}>
                    <FormRow direction='vertical' gap={4} size={TextBoxSize.FIT}>
                      <FormRow size={TextBoxSize.SMALL}>
                        <FormLabel
                          fixWidth={null}
                          help={
                            '購入した時のスコア。最も重要な行動なので、閲覧の3〜7倍程度が推奨です。'
                          }
                        >
                          purchase
                        </FormLabel>
                      </FormRow>
                      <Text color={TextColor.SORA} size={10}>
                        購入スコア
                      </Text>
                    </FormRow>
                    <FormTextField
                      type={FormTextFieldType.NUMBER}
                      size={TextBoxSize.SMALL}
                      register={register('collaborative.action_scores.purchase', {
                        valueAsNumber: true
                      })}
                      error={!!errors.collaborative?.action_scores?.purchase}
                    />
                  </FormRow>
                  <FormError
                    message={
                      errors.collaborative?.action_scores?.view?.message ||
                      errors.collaborative?.action_scores?.cart?.message ||
                      errors.collaborative?.action_scores?.purchase?.message
                    }
                  />
                </FormRow>
              </InsideArea>
            </AreaContent>
            <Line />
            <AreaContent>
              <FormRow size={TextBoxSize.FULL} direction='vertical' gap={2}>
                <Text tag='h3' weight='bold' lineHeight={150}>
                  ニューラル (NCF)
                </Text>
                <Text size={12}>
                  ニューラルネットワークを使っておすすめを出す仕組みです。データが多いほど、より高い精度が期待できます。
                </Text>
              </FormRow>
              <FormRow direction='vertical'>
                <FormRow>
                  <FormRow direction='vertical' gap={0} size={TextBoxSize.LARGE}>
                    <FormRow align='center' gap={4}>
                      <FormLabel
                        help='ユーザーと商品をベクトルに変換する次元数。商品数1,000以下なら16〜32、それ以上なら32〜64が目安です。'
                        fixWidth={null}
                      >
                        embed_dim
                      </FormLabel>
                      <Text size={10} color={TextColor.SUCCESS}>
                        埋め込み次元
                      </Text>
                    </FormRow>
                    <FormRow direction='vertical'>
                      <FormTextField
                        type={FormTextFieldType.NUMBER}
                        size={TextBoxSize.SMALL}
                        register={register('neural.embed_dim', { valueAsNumber: true })}
                        error={!!errors.neural?.embed_dim}
                      />
                      <FormError message={errors.neural?.embed_dim?.message} />
                    </FormRow>
                  </FormRow>
                  <FormRow direction='vertical' gap={0} size={TextBoxSize.LARGE}>
                    <FormRow align='center' gap={4}>
                      <FormLabel
                        help='学習データを何周繰り返すか。多すぎると過学習になります。5〜15が推奨です。'
                        fixWidth={null}
                      >
                        epochs
                      </FormLabel>
                      <Text size={10} color={TextColor.SUCCESS}>
                        データの学習回数
                      </Text>
                    </FormRow>
                    <FormRow direction='vertical'>
                      <FormTextField
                        type={FormTextFieldType.NUMBER}
                        size={TextBoxSize.SMALL}
                        register={register('neural.epochs', { valueAsNumber: true })}
                        error={!!errors.neural?.epochs}
                      />
                      <FormError message={errors.neural?.epochs?.message} />
                    </FormRow>
                  </FormRow>
                  <FormRow direction='vertical' gap={0} size={TextBoxSize.LARGE}>
                    <FormRow align='center' gap={4}>
                      <FormLabel
                        help='1回の学習でどれだけパラメータを更新するか。通常は0.001で問題ありません。'
                        fixWidth={null}
                      >
                        learning_rate
                      </FormLabel>
                      <Text size={10} color={TextColor.SUCCESS}>
                        データ学習率
                      </Text>
                    </FormRow>
                    <FormRow direction='vertical'>
                      <FormTextField
                        type={FormTextFieldType.NUMBER}
                        size={TextBoxSize.SMALL}
                        register={register('neural.learning_rate', { valueAsNumber: true })}
                        error={!!errors.neural?.learning_rate}
                      />
                      <FormError message={errors.neural?.learning_rate?.message} />
                    </FormRow>
                  </FormRow>
                </FormRow>
                <FormRow>
                  <FormRow direction='vertical' gap={0} size={TextBoxSize.LARGE}>
                    <FormRow align='center' gap={4}>
                      <FormLabel
                        help='一度に処理するデータの件数。データ量が少ない場合は128、多い場合は256〜512が推奨です。'
                        fixWidth={null}
                      >
                        batch_size
                      </FormLabel>
                      <Text size={10} color={TextColor.SUCCESS}>
                        一度に処理する件数
                      </Text>
                    </FormRow>
                    <FormRow direction='vertical'>
                      <FormTextField
                        type={FormTextFieldType.NUMBER}
                        size={TextBoxSize.SMALL}
                        register={register('neural.batch_size', { valueAsNumber: true })}
                        error={!!errors.neural?.batch_size}
                      />
                      <FormError message={errors.neural?.batch_size?.message} />
                    </FormRow>
                  </FormRow>
                  <FormRow direction='vertical' gap={0} size={TextBoxSize.LARGE}>
                    <FormRow align='center' gap={4}>
                      <FormLabel
                        help='正例1件に対して生成する負例の数。多いほど「おすすめしない」判定の精度が上がります。3〜5が推奨です。'
                        fixWidth={null}
                      >
                        neg_ratio
                      </FormLabel>
                      <Text size={10} color={TextColor.SUCCESS}>
                        間違い例の数
                      </Text>
                    </FormRow>
                    <FormRow direction='vertical'>
                      <FormTextField
                        type={FormTextFieldType.NUMBER}
                        size={TextBoxSize.SMALL}
                        register={register('neural.neg_ratio', { valueAsNumber: true })}
                        error={!!errors.neural?.neg_ratio}
                      />
                      <FormError message={errors.neural?.neg_ratio?.message} />
                    </FormRow>
                  </FormRow>
                  <FormRow direction='vertical' gap={0} size={TextBoxSize.LARGE}>
                    <FormRow align='center' gap={4}>
                      <FormLabel
                        help='学習中にランダムにニューロンを無効化する割合。過学習を防ぎます。0.1〜0.3が一般的です。'
                        fixWidth={null}
                      >
                        dropout
                      </FormLabel>
                      <Text size={10} color={TextColor.SUCCESS}>
                        ニューロン無効化率
                      </Text>
                    </FormRow>
                    <FormRow direction='vertical'>
                      <FormTextField
                        type={FormTextFieldType.NUMBER}
                        size={TextBoxSize.SMALL}
                        register={register('neural.dropout', { valueAsNumber: true })}
                        error={!!errors.neural?.dropout}
                      />
                      <FormError message={errors.neural?.dropout?.message} />
                    </FormRow>
                  </FormRow>
                </FormRow>
              </FormRow>
              <FormRow direction='vertical' gap={2} size={TextBoxSize.FULL}>
                <FormLabel>mlp_layers</FormLabel>
                <Text size={12}>
                  ニューラルネットワークの層構成をカンマ区切りで指定します。左から右へ数値が小さくなるよう設定してください（例:
                  128,64,32）
                </Text>
                <FormRow>
                  <Controller
                    control={control}
                    name='neural.mlp_layers'
                    render={({ field }) => (
                      <TextField
                        size={TextBoxSize.MEDIUM}
                        value={(field.value ?? []).join(',')}
                        onChange={(e) => {
                          const arr = e.target.value
                            .split(',')
                            .map((s) => parseInt(s.trim()))
                            .filter((n) => !isNaN(n) && n > 0)
                          if (arr.length > 0) field.onChange(arr)
                        }}
                        onBlur={field.onBlur}
                        inputRef={field.ref}
                        error={!!errors.neural?.mlp_layers}
                      />
                    )}
                  />
                </FormRow>
                <FormError message={errors.neural?.mlp_layers?.message} />
              </FormRow>
            </AreaContent>
            <Line />
            <AreaContent>
              <FormRow size={TextBoxSize.FULL} direction='vertical' gap={2}>
                <Text tag='h3' weight='bold' lineHeight={150}>
                  コンテンツベース (TF-IDF)
                </Text>
                <Text size={12}>
                  ニューラルネットワークを使っておすすめを出す仕組み商品名・カテゴリ・説明文などのテキスト情報を分析し、似た特徴を持つ商品を推薦します。
                </Text>
              </FormRow>
              <FormRow direction='vertical' gap={2}>
                <FormRow align='center' gap={4}>
                  <FormLabel
                    help={
                      'テキスト分析で使用する単語の最大数。商品数1,000以下なら3,000〜5,000、それ以上なら5,000〜10,000が目安です。'
                    }
                    fixWidth={null}
                  >
                    max_features
                  </FormLabel>
                  <Text size={10} color={TextColor.KAKI}>
                    分析使用単語の最大数
                  </Text>
                </FormRow>
                <FormTextField
                  type={FormTextFieldType.NUMBER}
                  size={TextBoxSize.SMALL}
                  register={register('content_based.max_features', { valueAsNumber: true })}
                  error={!!errors.content_based?.max_features}
                />
                <FormError message={errors.content_based?.max_features?.message} />
              </FormRow>
            </AreaContent>
          </Area>
          {/* Learning data structure */}
          <DataStructurePanel isW2Full={isW2Full} />
        </Content>
        <Side>
          <Area>
            <Heading
              subText={
                <>
                  3つのエンジンの配合比率です。合計が常に
                  <br />
                  1.0（100%）になるよう自動調整されます。
                </>
              }
            >
              モデル配分比率
            </Heading>
            <AreaContent>
              <FormRow direction='vertical' size={TextBoxSize.FULL}>
                <FormRow direction='vertical' size={TextBoxSize.FULL} gap={0}>
                  <FormLabel
                    help={
                      '「この商品を買った人はこれも買っています」のような、ユーザー行動の類似パターンに基づく推薦です。'
                    }
                    fixWidth={null}
                  >
                    協調フィルタリング
                  </FormLabel>
                  <Controller
                    control={control}
                    name='hybrid_weights.collaborative'
                    render={({ field }) => (
                      <Slider
                        value={field.value}
                        color='#6ECBFA'
                        onChange={(v) =>
                          setValue(
                            'hybrid_weights',
                            normalizeHybridWeights('collaborative', v, getValues('hybrid_weights')),
                            { shouldDirty: true }
                          )
                        }
                      />
                    )}
                  />
                </FormRow>
                <FormRow direction='vertical' gap={0}>
                  <FormLabel
                    help={
                      '深層学習で複雑なユーザーと商品の関係を捉えます。データ量が多いほど精度が上がります。'
                    }
                    fixWidth={null}
                  >
                    ニューラル (NCF)
                  </FormLabel>
                  <Controller
                    control={control}
                    name='hybrid_weights.neural'
                    render={({ field }) => (
                      <Slider
                        value={field.value}
                        color='#57C6B1'
                        onChange={(v) =>
                          setValue(
                            'hybrid_weights',
                            normalizeHybridWeights('neural', v, getValues('hybrid_weights')),
                            { shouldDirty: true }
                          )
                        }
                      />
                    )}
                  />
                </FormRow>
                <FormRow direction='vertical' size={TextBoxSize.FULL} gap={0}>
                  <FormLabel
                    help={'商品名やカテゴリなどのテキスト情報から類似商品を見つけます。'}
                    fixWidth={null}
                  >
                    コンテンツベース
                  </FormLabel>
                  <Controller
                    control={control}
                    name='hybrid_weights.content_based'
                    render={({ field }) => (
                      <Slider
                        value={field.value}
                        color='#FFB800'
                        onChange={(v) =>
                          setValue(
                            'hybrid_weights',
                            normalizeHybridWeights('content_based', v, getValues('hybrid_weights')),
                            { shouldDirty: true }
                          )
                        }
                      />
                    )}
                  />
                </FormRow>
              </FormRow>
              <MessageBar type='success' iconType={IconType.BEGINNER}>
                <span className='flex items-center gap-2'>
                  <Text size={14} weight='bold' color={TextColor.SUCCESS} lineHeight={150}>
                    配分比率に迷ったら
                  </Text>
                </span>
                <Text size={12}>
                  一般的なECサイトでは、協調40%：ニューラル40%：コンテンツ20%が推奨です。
                </Text>
              </MessageBar>
            </AreaContent>
          </Area>
        </Side>
      </ContentSideFrame>
    </form>
  )
}

export default ModelTuningForm
